import React, { useCallback, useMemo } from 'react';
import {
  BaseCampaignItem,
  BaseCampaignItemPatchDTO,
  BaseMgSupplierCampaignItem,
  BaseMgSupplierCampaignItemLookup,
  BaseMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CampaignTenantType } from '@retail/campaign/data-access';
import { useMountEffect } from '@shared/hooks';
import {
  AddCampaignItemsSchema,
  BaseCampaignItemSchema,
  GetCampaignItemsColumnsFn,
  ImportItemApiErrors,
} from '@retail/campaign/components';
import { AddItemsOverviewContainer } from './AddItems/AddItemsOverviewContainer';
import { useEditCampaignItemsActions } from '@retail/campaign/context';
import { SubmitErrorHandler, SubmitHandler, useFormContext } from 'react-hook-form';
import { useAppTFunction } from '@retail/app/i18n';
import { useSearchParams } from 'react-router';
import { BodyLoader, withDialogSuspenseErrorBoundary } from '@retail/components';

interface Props<
  TCampaignItem extends BaseCampaignItem,
  TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem,
  TPutItemDTO extends BaseCampaignItemPatchDTO,
  TLookupDTO extends BaseMgSupplierCampaignItemLookupDTO,
  TItemSchema extends BaseCampaignItemSchema
> {
  campaignId: string;
  campaignItems: TCampaignItem[];
  open: boolean;
  onClose: () => void;
  type: CampaignTenantType;
  getCampaignItemsColumns: GetCampaignItemsColumnsFn<TMgSupplierCampaignItem>;
  transformToPutDTO: (
    formItem: TItemSchema,
    lookupItem: TMgSupplierCampaignItem,
    campaignItem: TCampaignItem
  ) => TPutItemDTO;
  isPendingLookupItems: boolean;
  performItemsLookup: (lookupSearchItems: MgSupplierItemLookupDTO[]) => void;
  lookupItemErrors: TLookupDTO[];
  lookupItems: TMgSupplierCampaignItem[];
  setLookupItems: (items: TMgSupplierCampaignItem[]) => void;
}

function EditCampaignItemsDialogComponent<
  TCampaignItem extends BaseCampaignItem,
  TMgSupplierCampaignItem extends BaseMgSupplierCampaignItem,
  TPutItemDTO extends BaseCampaignItemPatchDTO,
  TLookupDTO extends BaseMgSupplierCampaignItemLookupDTO,
  TItemSchema extends BaseCampaignItemSchema
>({
  campaignId,
  campaignItems,
  open,
  onClose,
  type,
  getCampaignItemsColumns,
  transformToPutDTO,
  isPendingLookupItems,
  performItemsLookup,
  lookupItemErrors,
  lookupItems,
  setLookupItems,
}: Props<TCampaignItem, TMgSupplierCampaignItem, TPutItemDTO, TLookupDTO, TItemSchema>) {
  const t = useAppTFunction();
  const [, setURLSearchParams] = useSearchParams();

  const formMethods = useFormContext<AddCampaignItemsSchema<TItemSchema>>();

  const { handleEditItems, isLoadingEditToCampaign: isSubmitting } =
    useEditCampaignItemsActions<TPutItemDTO>({
      campaignId,
      type,
    });

  useMountEffect(() => {
    const lookupSearchItems: MgSupplierItemLookupDTO[] = campaignItems.map((item) => ({
      mgItemNumber: item.mgItemNumber,
      supplierId: item.mgSupplierId,
    }));

    performItemsLookup(lookupSearchItems);
  });

  const onSubmit: SubmitHandler<AddCampaignItemsSchema<TItemSchema>> = useCallback(
    ({ items }) => {
      const itemEntries = Object.entries(items);
      const dto: TPutItemDTO[] = itemEntries.map(([mgSupplierItemId, formItem]) => {
        const searchItem = lookupItems!.find((i) => i.mgSupplierItemId + '' === mgSupplierItemId)!;
        const campaignItem = campaignItems.find(
          (i) => `${i.mgSupplierItemId}` === mgSupplierItemId
        )!;
        return transformToPutDTO(formItem, searchItem, campaignItem);
      });

      handleEditItems(dto).then(onClose);
    },
    [campaignItems, handleEditItems, lookupItems, onClose, transformToPutDTO]
  );

  const onSubmitErrorHandler: SubmitErrorHandler<AddCampaignItemsSchema<TItemSchema>> = useCallback(
    (errors) => {
      const errorItemNumbers = errors.items ? Object.keys(errors.items) : [];

      const itemsSortedErrorsFirst = [...(lookupItems ?? [])].sort((a, b) => {
        const aIndex = errorItemNumbers.indexOf(a.mgSupplierItemId + '');
        const bIndex = errorItemNumbers.indexOf(b.mgSupplierItemId + '');
        return bIndex - aIndex;
      });
      setLookupItems(itemsSortedErrorsFirst);
      setURLSearchParams();
    },
    [lookupItems, setURLSearchParams]
  );

  const submitItemPrices = formMethods.handleSubmit(onSubmit, onSubmitErrorHandler);

  const lookupStatus = useMemo(() => {
    if (lookupItemErrors.length > 0) return 'error';
    if (isPendingLookupItems) return 'loading';
    return 'success';
  }, [lookupItemErrors, isPendingLookupItems]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="3xl"
      slotProps={{ paper: { sx: { height: '90vh' } } }}
    >
      <DialogTitle>{t('campaign.actions.editItems')}</DialogTitle>
      <DialogContent>
        {lookupStatus === 'error' && (
          <ImportItemApiErrors
            items={(lookupItemErrors as any[]).map((item) => ({
              reason: item.errorCode!,
              ...item.input,
            }))}
          />
        )}
        {lookupStatus === 'loading' && <BodyLoader />}
        {lookupStatus === 'success' && (
          <AddItemsOverviewContainer
            items={lookupItems}
            getCampaignItemsColumns={getCampaignItemsColumns}
          />
        )}
      </DialogContent>
      <DialogActions>
        <TextButton onClick={onClose} size="small" disabled={lookupStatus !== 'success'}>
          {t('campaign.cancel')}
        </TextButton>

        <ContainedButton
          disabled={lookupStatus !== 'success'}
          loading={isSubmitting}
          onClick={submitItemPrices}
          size="small"
        >
          {t('campaign.actions.addItemsToCampaign')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

export const EditCampaignItemsDialog = withDialogSuspenseErrorBoundary(
  EditCampaignItemsDialogComponent
) as typeof EditCampaignItemsDialogComponent;
